import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Anchor, Trophy, Star, Target, MessageCircle, Zap } from 'lucide-react';
import { triggerCaptain } from '../components/CaptainMascot';

const AnimationContext = createContext();

// Messages d'encouragement thématiques
const encouragementMessages = [
  { text: "Cap vers la liberté financière !", icon: Compass },
  { text: "Encore un pas vers ton objectif !", icon: Target },
  { text: "Tu avances bien, continue !", icon: Anchor },
  { text: "La persévérance paie toujours !", icon: Star },
  { text: "Bravo pour ton engagement !", icon: Trophy },
];

export const AnimationProvider = ({ children }) => {
  const [xpAnimation, setXpAnimation] = useState(null);
  const [levelUpAnimation, setLevelUpAnimation] = useState(null);
  const [badgeAnimation, setBadgeAnimation] = useState(null);
  const [confetti, setConfetti] = useState(false);
  const [encouragement, setEncouragement] = useState(null);
  const [compassActive, setCompassActive] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState(false);

  // Animation XP gain
  const showXpGain = useCallback((amount) => {
    setXpAnimation({ amount, id: Date.now() });
    setTimeout(() => setXpAnimation(null), 2000);
    
    // Trigger Captain reaction
    triggerCaptain('xp');
    
    // Show encouragement randomly (20% chance)
    if (Math.random() < 0.2) {
      const msg = encouragementMessages[Math.floor(Math.random() * encouragementMessages.length)];
      setEncouragement({ ...msg, id: Date.now() });
      setTimeout(() => setEncouragement(null), 3000);
    }
  }, []);

  // Animation Level Up
  const showLevelUp = useCallback((newLevel) => {
    setLevelUpAnimation({ level: newLevel, id: Date.now() });
    setConfetti(true);
    
    // Trigger Captain reaction
    triggerCaptain('level_up');
    
    setTimeout(() => {
      setLevelUpAnimation(null);
      setConfetti(false);
    }, 3000);
  }, []);

  // Animation Badge
  const showBadgeEarned = useCallback((badgeName) => {
    setBadgeAnimation({ name: badgeName, id: Date.now() });
    setConfetti(true);
    
    // Trigger Captain reaction
    triggerCaptain('badge');
    
    setTimeout(() => {
      setBadgeAnimation(null);
      setConfetti(false);
    }, 3000);
  }, []);

  // Animation Challenge Complete
  const showChallengeComplete = useCallback((challengeName, xpReward) => {
    setConfetti(true);
    showXpGain(xpReward);
    setTimeout(() => setConfetti(false), 2500);
  }, [showXpGain]);

  // Animation Victory Shared
  const showVictoryShared = useCallback(() => {
    setConfetti(true);
    triggerCaptain('victory');
    setTimeout(() => setConfetti(false), 2500);
  }, []);

  // Animation Navigation (Compass)
  const triggerNavigation = useCallback(() => {
    setCompassActive(true);
    setTimeout(() => setCompassActive(false), 1000);
  }, []);

  // Animation Message Sent
  const showMessageSent = useCallback(() => {
    setMessageSuccess(true);
    setTimeout(() => setMessageSuccess(false), 1500);
  }, []);

  return (
    <AnimationContext.Provider value={{
      showXpGain,
      showLevelUp,
      showBadgeEarned,
      showChallengeComplete,
      showVictoryShared,
      triggerNavigation,
      showMessageSent,
    }}>
      {children}
      
      {/* XP Gain Animation */}
      <AnimatePresence>
        {xpAnimation && (
          <motion.div
            key={xpAnimation.id}
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: -30, scale: 1 }}
            exit={{ opacity: 0, y: -60 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="fixed top-24 right-8 z-[100] pointer-events-none"
          >
            <div className="flex items-center gap-2 bg-gradient-to-r from-amber-500/90 to-yellow-500/90 text-white px-4 py-2 rounded-full shadow-lg backdrop-blur-sm">
              <Zap className="w-5 h-5" />
              <span className="font-bold text-lg">+{xpAnimation.amount} XP</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Level Up Animation */}
      <AnimatePresence>
        {levelUpAnimation && (
          <motion.div
            key={levelUpAnimation.id}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
          >
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, ease: "linear" }}
                className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center shadow-2xl"
              >
                <Star className="w-12 h-12 text-white" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-heading font-bold text-amber-500"
              >
                Niveau {levelUpAnimation.level}
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-lg text-muted-foreground mt-2"
              >
                Félicitations !
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Badge Earned Animation */}
      <AnimatePresence>
        {badgeAnimation && (
          <motion.div
            key={badgeAnimation.id}
            initial={{ opacity: 0, scale: 0.5, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{ duration: 0.5, type: "spring" }}
            className="fixed top-1/3 left-1/2 -translate-x-1/2 z-[100] pointer-events-none"
          >
            <div className="bg-card border border-amber-500/50 rounded-2xl p-6 shadow-2xl text-center">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: 2 }}
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center"
              >
                <Trophy className="w-10 h-10 text-white" />
              </motion.div>
              <p className="text-xl font-heading font-bold text-amber-500">Nouveau Badge !</p>
              <p className="text-muted-foreground mt-1">{badgeAnimation.name}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confetti Effect (Golden, subtle) */}
      <AnimatePresence>
        {confetti && (
          <div className="fixed inset-0 z-[99] pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  opacity: 1, 
                  y: -20, 
                  x: Math.random() * window.innerWidth,
                  rotate: 0,
                  scale: Math.random() * 0.5 + 0.5
                }}
                animate={{ 
                  opacity: 0, 
                  y: window.innerHeight + 100,
                  rotate: Math.random() * 360,
                }}
                transition={{ 
                  duration: 2 + Math.random(), 
                  ease: "easeIn",
                  delay: Math.random() * 0.5
                }}
                className="absolute w-3 h-3 rounded-sm"
                style={{
                  background: i % 2 === 0 
                    ? 'linear-gradient(135deg, #F59E0B, #D97706)' 
                    : 'linear-gradient(135deg, #FBBF24, #F59E0B)',
                }}
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Encouragement Message */}
      <AnimatePresence>
        {encouragement && (
          <motion.div
            key={encouragement.id}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed bottom-8 right-8 z-[100] pointer-events-none"
          >
            <div className="flex items-center gap-3 bg-card/95 border border-amber-500/30 px-5 py-3 rounded-xl shadow-lg backdrop-blur-sm">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <encouragement.icon className="w-5 h-5 text-amber-500" />
              </div>
              <p className="text-sm font-medium">{encouragement.text}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Compass */}
      <AnimatePresence>
        {compassActive && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] pointer-events-none"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center shadow-lg"
            >
              <Compass className="w-6 h-6 text-white" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message Sent Success */}
      <AnimatePresence>
        {messageSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-8 z-[100] pointer-events-none"
          >
            <div className="flex items-center gap-2 bg-green-500/90 text-white px-4 py-2 rounded-full shadow-lg">
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Message envoyé</span>
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                ✓
              </motion.span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimationContext.Provider>
  );
};

export const useAnimations = () => {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error('useAnimations must be used within AnimationProvider');
  }
  return context;
};

export default AnimationProvider;
